import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  LYRRAONE_LOGO_ALT,
  LYRRAONE_LOGO_ICON_ALT,
  LYRRAONE_LOGO_ICON_SRC,
  LYRRAONE_LOGO_SRC,
} from '@/lib/lyrraone-brand';

const SIZE_CLASS = {
  xs: 'h-7',
  sm: 'h-8',
  md: 'h-10',
  lg: 'h-12 md:h-14',
  xl: 'h-14 md:h-16',
} as const;

const ICON_SIZE_CLASS = {
  xs: 'h-7 w-7',
  sm: 'h-8 w-8',
  md: 'h-9 w-9',
  lg: 'h-10 w-10',
  xl: 'h-12 w-12',
} as const;

export type LyrraOneLogoSize = keyof typeof SIZE_CLASS;

interface LyrraOneLogoProps {
  size?: LyrraOneLogoSize;
  /** `full` = logo complet ; `icon` = icône seule (`logo small.png`). */
  variant?: 'full' | 'icon';
  className?: string;
  /** Lien de navigation ; `false` = image seule (ex. dans un NavLink parent). */
  linkTo?: string | false;
  /** Sur fond sombre : cadre clair (logo complet uniquement). */
  onDark?: boolean;
}

export function LyrraOneLogo({
  size = 'md',
  variant = 'full',
  className,
  linkTo = '/',
  onDark = false,
}: LyrraOneLogoProps) {
  const isIcon = variant === 'icon';
  const src = isIcon ? LYRRAONE_LOGO_ICON_SRC : LYRRAONE_LOGO_SRC;
  const alt = isIcon ? LYRRAONE_LOGO_ICON_ALT : LYRRAONE_LOGO_ALT;

  const image = (
    <img
      src={src}
      alt={alt}
      className={cn(
        'object-contain shrink-0',
        isIcon
          ? cn(ICON_SIZE_CLASS[size], 'object-center', className)
          : cn('w-auto max-w-full object-left', SIZE_CLASS[size], className),
      )}
      decoding="async"
    />
  );

  const content = onDark && !isIcon ? (
    <span className="inline-flex rounded-xl bg-white/95 px-3 py-2 shadow-sm">{image}</span>
  ) : (
    image
  );

  if (linkTo) {
    return (
      <Link to={linkTo} className="inline-flex shrink-0" aria-label={LYRRAONE_LOGO_ALT}>
        {content}
      </Link>
    );
  }

  return <span className="inline-flex shrink-0">{content}</span>;
}

/** Icône LyrraOne seule — alias de `LyrraOneLogo variant="icon"`. */
export function LyrraOneLogoIcon({
  size = 'sm',
  className,
  linkTo = false as const,
}: Pick<LyrraOneLogoProps, 'size' | 'className' | 'linkTo'>) {
  return <LyrraOneLogo size={size} variant="icon" className={className} linkTo={linkTo} />;
}

/** @deprecated Utiliser `LyrraOneLogo variant="icon"` */
export function NovaIcon({ className, size = 40 }: { className?: string; size?: number }) {
  return (
    <img
      src={LYRRAONE_LOGO_ICON_SRC}
      alt=""
      aria-hidden
      className={cn('object-contain object-center', className)}
      style={{ height: size, width: size }}
    />
  );
}

export default LyrraOneLogo;
